// The action row under a Wixel asset in an assistant chat message: "Edit in Wixel" and "Get a design review",
// visible on the attachment itself instead of only behind the viewer's "•••" menu (owner feedback 2026-10-27:
// a Wixel image in the chat showed only reply/copy/react, so nobody found the editor).
//
// ONE presence rule on all three surfaces (iOS `WixelChatAssetActions`, Android `WixelChatAssetActions`):
//   - the message is the AGENT's (an upload of the user's own is theirs, not an asset Symphony made), AND
//   - the attachment has arrived (a placeholder has no address yet), AND
//   - the attachment's OWN URL is a Wixel short link — the same parser Edit-in-Wixel and the design-review
//     gate use (`isWixelShortLink`). The server attaches a Wixel asset where it lives (`/link/<slug>/raw`), so
//     the URL is the whole test; nothing has to be resolved first.
//
// "Edit in Wixel" opens the editor through `editInWixelLink` (desktop: a new tab; phone/tablet browser: the
// app-or-browser sheet). "Get a design review" sends the design-review request into THIS conversation, as a
// normal user turn — the asset is already in the thread the agent is reading, so a new chat (what the file
// screens open, `designReviewHandoff`) would only take the reader away from it. It is omitted on a thread that
// accepts no sends (a channel-locked one), and disabled while a turn streams, the same gate every in-chat card
// that sends applies.

import type { MessageAttachment } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { isPlaceholderAttachment } from './attachment-placeholder';
import { isWixelShortLink, wixelEditUrlFromAssetUrl } from './wixel-link';

/** BI button names, the same on all three surfaces (page `chat`). */
export const WIXEL_CHAT_ASSET_BI = { edit: 'edit_in_wixel', designReview: 'design_review' } as const;

/** The editor URL for an attachment that carries the row, or null when it carries none. See the module header. */
export function wixelChatAssetEditorUrl(
  att: Pick<MessageAttachment, 'url' | 'vfsPath'>,
  agentAuthored: boolean,
): string | null {
  if (!agentAuthored || isPlaceholderAttachment(att)) return null;
  if (!isWixelShortLink(att.url)) return null;
  return wixelEditUrlFromAssetUrl(att.url);
}
